import { Button, Skeleton } from '@mantine/core';
import { IconAlertTriangle, IconChecklist, IconRefresh } from '@tabler/icons-react';
import { useTranslation } from 'react-i18next';
import { EmptyState } from '../../EmptyState.js';
import { formatDay } from '../../format.js';
import panel from '../../Panel.module.css';
import { StatusBadge } from '../../StatusBadge.js';
import { useShell } from '../../shell-context.js';
import { AlertList } from '../AlertList.js';
import { alertPath, type OfficeDashboardSummary } from '../dashboard-types.js';
import classes from './TasksPanel.module.css';
import { TaskGroupView } from './task-view.js';

/** Panelin çapası: dar ekranda sayfa başındaki "N iş sizi bekliyor" bağlantısı buraya atlar. */
export const ACTIVE_ALERTS_ID = 'active-alerts';

export interface TasksPanelProps {
  /** Pano özeti: `undefined` yükleniyor, `null` okunamadı. */
  summary: OfficeDashboardSummary | null | undefined;
  /** "Yeniden dene" ve "Yenile": panonun tek çağrısını yeniden yapar. */
  onRefresh: () => void;
  /** Dolacak belgenin gün sayısı için bugün (`YYYY-MM-DD`). */
  today?: string;
}

/**
 * Active Alerts paneli (ADR 0156): üstte "Sizi bekleyen işler" grupları (kişinin kararını ya da
 * işlemini bekleyen kayıtlar), altta uyarılar. Başlıkta toplam ve gecikmiş rozetleri. Satır kaydın
 * kendi ekranını açar; karar panodan verilmez (#2240).
 */
export function TasksPanel({ summary, onRefresh, today }: TasksPanelProps) {
  const { t } = useTranslation();
  const shell = useShell();
  const tasks = summary?.tasks;
  const titleId = `${ACTIVE_ALERTS_ID}-title`;
  return (
    <section
      id={ACTIVE_ALERTS_ID}
      className={[panel.panel, classes.panel].join(' ')}
      data-headed
      data-divider
      aria-labelledby={titleId}
      aria-busy={summary === undefined || undefined}
    >
      <div className={panel.head}>
        <div className={panel.heading}>
          {/* Odak alabilir: sayfa başındaki "panele git" bağlantısı odağı buraya taşır */}
          <h2 id={titleId} className={panel.title} tabIndex={-1}>
            {t('alerts.title')}
          </h2>
        </div>
        <div className={classes.badges}>
          {tasks && tasks.total > 0 ? (
            <StatusBadge tone="info">{t('tasks.badge', { count: tasks.total })}</StatusBadge>
          ) : null}
          {tasks && tasks.overdue > 0 ? (
            <StatusBadge tone="danger">
              {t('tasks.overdueBadge', { count: tasks.overdue })}
            </StatusBadge>
          ) : null}
        </div>
      </div>
      <div className={panel.body}>
        {summary === undefined ? (
          <div className={classes.loading}>
            <span className={classes.srOnly}>{t('tasks.loading')}</span>
            <Skeleton height={18} width="60%" />
            <Skeleton height={44} />
            <Skeleton height={44} />
            <Skeleton height={120} />
          </div>
        ) : summary === null ? (
          <EmptyState
            tone="danger"
            title={t('tasks.error')}
            action={
              <Button
                variant="default"
                size="xs"
                leftSection={<IconRefresh size={14} />}
                onClick={onRefresh}
              >
                {t('tasks.retry')}
              </Button>
            }
          />
        ) : (
          <>
            {tasks ? (
              <section className={classes.part} aria-labelledby={`${ACTIVE_ALERTS_ID}-tasks`}>
                <h3 id={`${ACTIVE_ALERTS_ID}-tasks`} className={classes.partTitle}>
                  {t('tasks.heading')}
                </h3>
                {/* Bir kaynak okunamadı: okunan gruplar gösterilir, şeritte "Yenile" */}
                {tasks.partial ? (
                  <div className={classes.partial} role="status">
                    <IconAlertTriangle size={14} aria-hidden />
                    <span className={classes.partialText}>{t('tasks.partial')}</span>
                    <Button
                      variant="subtle"
                      size="compact-xs"
                      leftSection={<IconRefresh size={13} />}
                      onClick={onRefresh}
                    >
                      {t('tasks.refresh')}
                    </Button>
                  </div>
                ) : null}
                {tasks.groups.length === 0 ? (
                  <EmptyState
                    className={classes.empty}
                    icon={<IconChecklist size={22} stroke={1.8} />}
                    title={t('tasks.none')}
                  />
                ) : (
                  tasks.groups.map((group) => <TaskGroupView key={group.kind} group={group} />)
                )}
              </section>
            ) : null}
            {/* Başlık yalnız iş bölümü varken çizilir; yoksa bölüm panelin başlığıyla okunur */}
            <section
              className={classes.part}
              {...(tasks ? { 'aria-labelledby': `${ACTIVE_ALERTS_ID}-alerts` } : {})}
            >
              {tasks ? (
                <h3 id={`${ACTIVE_ALERTS_ID}-alerts`} className={classes.partTitle}>
                  {t('tasks.alertsHeading')}
                </h3>
              ) : null}
              <AlertList
                alerts={summary.alerts}
                limit={6}
                formatDate={formatDay}
                {...(today ? { today } : {})}
                {...(shell ? { onOpen: (alert) => shell.navigate(alertPath(alert)) } : {})}
              />
            </section>
          </>
        )}
      </div>
    </section>
  );
}
